import { useEffect, useState } from "react";
import { TbX } from "react-icons/tb";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import BtnPrimaryM from "../Buttons/BtnPrimaryM";
import IconCircle from "./IconCircle";
import PropTypes from "prop-types";

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const Popup = ({
  isOpen,
  onClose,
  title,
  children,
  description,
  buttonSecondaryCTA,
  onClickSecondaryLink,
  buttonPrimaryCTA,
  onClickPrimaryLink,
}) => {
  const isMobile = useIsMobile();
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (
      e.target.classList.contains("popup-overlay") &&
      window.getSelection().toString() === ""
    ) {
      onClose();
    }
  };

  // --- Mobile Sheet Style ---
  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-end md:hidden popup-overlay bg-black/30"
        onClick={handleOverlayClick}
      >
        <div className="relative w-full bg-white rounded-t-xl shadow-2xl p-4 pb-8 card-base-border animate-slide-up">
          {/* X button */}
          <div className="flex justify-end z-20 mb-2">
            <IconCircle icon={TbX} onClick={onClose} ariaLabel="Close" />
          </div>
          {/* Drag handle and content */}
          <div className="flex flex-col gap-4">
            <h2 className="text-xl md:text-lg font-bold mt-2">{title}</h2>
            {description && <p className="text-sm mb-2">{description}</p>}
            {children}
            <div className="flex flex-col gap-2 mt-4 w-full">
              {buttonSecondaryCTA && (
                <BtnSecondaryM
                  buttonCTA={buttonSecondaryCTA}
                  onClickLink={onClickSecondaryLink}
                  type="button"
                  className="w-full"
                />
              )}
              <BtnPrimaryM
                buttonCTA={buttonPrimaryCTA}
                onClickLink={onClickPrimaryLink}
                type="submit"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Desktop Classic Modal ---
  return (
    <div
      className="fixed top-0 left-0 w-full h-full bg-black/50 flex items-center justify-center z-50 popup-overlay"
      onClick={handleOverlayClick}
    >
      <div className="bg-white p-1.5 rounded-md w-[40vw] max-w-[90%] shadow-md relative overflow-hidden">
        <div className="relative card-base card-base-border background-page-color gap-4 flex flex-col rounded-md px-8 py-12 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl md:text-lg font-bold">{title}</h2>
          {description && <p className="text-sm mb-4">{description}</p>}
          {children}
          <button
            className="cursor-pointer absolute top-0 right-0 p-2"
            onClick={onClose}
          >
            <IconCircle icon={TbX} onClick={onClose} />
          </button>
          <div className="flex gap-2 items-end justify-end">
            {buttonSecondaryCTA && (
              <BtnSecondaryM
                buttonCTA={buttonSecondaryCTA}
                onClickLink={onClickSecondaryLink}
                type="button"
              />
            )}
            <BtnPrimaryM
              buttonCTA={buttonPrimaryCTA}
              onClickLink={onClickPrimaryLink}
              type="submit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Popup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  description: PropTypes.string,
  buttonSecondaryCTA: PropTypes.string,
  onClickSecondaryLink: PropTypes.func,
  buttonPrimaryCTA: PropTypes.string,
  onClickPrimaryLink: PropTypes.func,
};

export default Popup;
